import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaBold,
  FaCalendarAlt,
  FaCheckSquare,
  FaComments,
  FaEdit,
  FaListUl,
  FaPaperPlane,
  FaReply,
  FaTasks,
  FaTimes,
  FaTrash,
  FaUnderline,
  FaUserCircle,
} from "react-icons/fa";
import { toast } from "react-toastify";
import DevActivityService from "../../service/DevActivityService";
import ClassNXLoader from "../ClassNXLoader";
import {
  AREAS,
  CREATE_STATUSES,
  getTodayDateString,
  PRIORITIES,
  priorityBadgeStyle,
  STATUSES,
  validateActivityDates,
} from "../../utils/devActivityValidation";

const blankActivity = () => ({
  title: "",
  module: "Student Management",
  area: "Frontend + Backend",
  description: "",
  status: "not_started",
  priority: "medium",
  plannedStart: "",
  plannedEnd: "",
  actualStart: "",
  actualEnd: "",
});

const issueKey = (id) => `DEV-${String(id || "").padStart(3, "0")}`;

const formatTimestamp = (dateStr) => {
  if (!dateStr) return "Just now";
  try {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return String(dateStr);
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(dateStr);
  }
};

export default function SuperAdminDevActivityPage({ create = false }) {
  const { activityId } = useParams();
  const navigate = useNavigate();
  const [activity, setActivity] = useState(blankActivity);
  const [draft, setDraft] = useState(blankActivity);
  const [editing, setEditing] = useState(create);
  const [loading, setLoading] = useState(!create);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Comments state
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newCommentBody, setNewCommentBody] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [replyingToId, setReplyingToId] = useState(null);
  const [replyBody, setReplyBody] = useState("");
  const [submittingReply, setSubmittingReply] = useState(false);

  const loadComments = async (id = activityId) => {
    if (!id || create) return;
    setLoadingComments(true);
    try {
      const items = await DevActivityService.getComments(id);
      setComments(Array.isArray(items) ? items : []);
    } catch {
      // Keep comments view resilient if empty
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  useEffect(() => {
    if (create) return;
    let active = true;
    DevActivityService.getActivity(activityId)
      .then((item) => {
        if (active) {
          const next = {
            ...blankActivity(),
            ...item,
            priority: item?.priority || "medium",
          };
          setActivity(next);
          setDraft(next);
        }
      })
      .catch((error) => toast.error(error.message || "Unable to load activity."))
      .finally(() => {
        if (active) setLoading(false);
      });

    loadComments(activityId);

    return () => {
      active = false;
    };
  }, [activityId, create]);

  const setValue = (key, value) => {
    setDraft((current) => {
      const next = { ...current, [key]: value };
      // When status changes to completed, automatically pre-fill actualEnd if not set
      if (key === "status" && value === "completed" && !next.actualEnd) {
        next.actualEnd = getTodayDateString();
      }
      // When status changes to in_progress, pre-fill actualStart if not set
      if (key === "status" && value === "in_progress" && !next.actualStart) {
        next.actualStart = getTodayDateString();
      }
      return next;
    });
  };

  const beginEdit = () => {
    setDraft({ ...activity, priority: activity.priority || "medium" });
    setEditing(true);
  };

  const cancelEdit = () => {
    if (create) {
      navigate("/super-admin?panel=devActivityTracker");
    } else {
      setEditing(false);
    }
  };

  const save = async (event) => {
    event.preventDefault();
    if (!draft.title.trim() || saving) return;

    const dateError = validateActivityDates(draft, create, activity);
    if (dateError) {
      toast.error(dateError);
      return;
    }

    setSaving(true);
    const toastId = toast.loading(create ? "Creating activity..." : "Saving changes...");
    try {
      const payload = {
        ...draft,
        priority: draft.priority || "medium",
      };
      if (create) {
        delete payload.actualEnd;
      }
      const saved = create
        ? await DevActivityService.createActivity(payload)
        : await DevActivityService.updateActivity(activityId, payload);
      const next = { ...draft, ...saved, id: saved?.id || activityId };
      toast.update(toastId, {
        render: create ? "Activity created." : "Activity updated.",
        type: "success",
        isLoading: false,
        autoClose: 2500,
      });
      if (create) {
        navigate("/super-admin?panel=devActivityTracker", { replace: true });
      } else {
        setActivity(next);
        setDraft(next);
        setEditing(false);
      }
    } catch (error) {
      toast.update(toastId, {
        render: error.message || "Unable to save activity.",
        type: "error",
        isLoading: false,
        autoClose: 3500,
      });
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (status) => {
    if (activity.status === status) return;
    const previous = activity;
    const next = { ...activity, status };
    const today = getTodayDateString();

    if (status === "in_progress" && !next.actualStart) {
      next.actualStart = today;
    }
    if (status === "completed" && !next.actualEnd) {
      next.actualEnd = today;
    }

    const dateError = validateActivityDates(next, false, activity);
    if (dateError) {
      toast.error(dateError);
      return;
    }

    setActivity(next);
    const toastId = toast.loading("Updating status...");
    try {
      await DevActivityService.updateActivity(activityId, {
        status,
        actualStart: next.actualStart,
        actualEnd: next.actualEnd,
      });
      toast.update(toastId, { render: "Status updated.", type: "success", isLoading: false, autoClose: 2200 });
    } catch (error) {
      setActivity(previous);
      toast.update(toastId, {
        render: error.message || "Unable to update status.",
        type: "error",
        isLoading: false,
        autoClose: 3500,
      });
    }
  };

  const remove = async () => {
    setConfirmDelete(false);
    const toastId = toast.loading("Deleting activity...");
    try {
      await DevActivityService.deleteActivity(activityId);
      toast.update(toastId, { render: "Activity deleted.", type: "success", isLoading: false, autoClose: 2500 });
      navigate("/super-admin?panel=devActivityTracker", { replace: true });
    } catch (error) {
      toast.update(toastId, {
        render: error.message || "Unable to delete activity.",
        type: "error",
        isLoading: false,
        autoClose: 3500,
      });
    }
  };

  const handleAddComment = async (event) => {
    event.preventDefault();
    const text = newCommentBody.trim();
    if (!text || submittingComment) return;

    setSubmittingComment(true);
    const toastId = toast.loading("Posting comment...");
    try {
      const created = await DevActivityService.addComment(activityId, {
        body: text,
        parentCommentId: null,
      });
      setNewCommentBody("");
      toast.update(toastId, { render: "Comment posted.", type: "success", isLoading: false, autoClose: 2000 });
      if (created && created.id) {
        setComments((prev) => [...prev, created]);
      } else {
        await loadComments();
      }
    } catch (err) {
      toast.update(toastId, {
        render: err.message || "Unable to post comment.",
        type: "error",
        isLoading: false,
        autoClose: 3500,
      });
    } finally {
      setSubmittingComment(false);
    }
  };

  const handlePostReply = async (parentCommentId) => {
    const text = replyBody.trim();
    if (!text || submittingReply) return;

    setSubmittingReply(true);
    const toastId = toast.loading("Posting reply...");
    try {
      const created = await DevActivityService.addComment(activityId, {
        body: text,
        parentCommentId,
      });
      setReplyBody("");
      setReplyingToId(null);
      toast.update(toastId, { render: "Reply posted.", type: "success", isLoading: false, autoClose: 2000 });
      if (created && created.id) {
        setComments((prev) => [...prev, created]);
      } else {
        await loadComments();
      }
    } catch (err) {
      toast.update(toastId, {
        render: err.message || "Unable to post reply.",
        type: "error",
        isLoading: false,
        autoClose: 3500,
      });
    } finally {
      setSubmittingReply(false);
    }
  };

  const threadedComments = useMemo(() => {
    if (!Array.isArray(comments) || comments.length === 0) return [];
    const hasNested = comments.some((c) => Array.isArray(c.replies) && c.replies.length > 0);
    if (hasNested) {
      return comments.filter((c) => !c.parentCommentId);
    }

    const map = new Map();
    const roots = [];

    comments.forEach((c) => {
      map.set(String(c.id), { ...c, replies: [] });
    });

    comments.forEach((c) => {
      const item = map.get(String(c.id));
      if (c.parentCommentId && map.has(String(c.parentCommentId))) {
        map.get(String(c.parentCommentId)).replies.push(item);
      } else {
        roots.push(item);
      }
    });

    return roots;
  }, [comments]);

  if (loading) return <ClassNXLoader variant="page" label="Loading activity" detail="Opening issue details." />;

  const today = getTodayDateString();
  const availableStatuses = create ? CREATE_STATUSES : STATUSES;

  if (editing) {
    return (
      <main className="mx-auto w-full max-w-5xl px-4 py-6 sm:px-6 sm:py-8">
        <Link
          to={create ? "/super-admin?panel=devActivityTracker" : `/super-admin/dev-activities/${activityId}`}
          onClick={(event) => {
            event.preventDefault();
            cancelEdit();
          }}
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-blue-700"
        >
          <FaArrowLeft /> Back
        </Link>
        <form onSubmit={save} className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <header className="border-b border-slate-200 px-6 py-5 sm:px-8">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-600">Development project</p>
            <h1 className="mt-1 text-2xl font-bold text-slate-900">
              {create ? "Create activity" : `Edit ${issueKey(activityId)}`}
            </h1>
          </header>
          <div className="space-y-6 p-6 sm:p-8">
            <div className="grid gap-5 sm:grid-cols-2">
              <label className="text-sm font-semibold text-slate-700">
                Project
                <span className="mt-2 flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-3 text-slate-800">
                  <span className="flex h-7 w-7 items-center justify-center rounded bg-blue-600 text-white">
                    <FaTasks />
                  </span>{" "}
                  Development activities
                </span>
              </label>
              <label className="text-sm font-semibold text-slate-700">
                Issue type
                <span className="mt-2 flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-3 text-slate-800">
                  <FaCheckSquare className="text-blue-500" /> Task
                </span>
              </label>
            </div>
            <label className="block text-sm font-semibold text-slate-700">
              Summary <span className="text-rose-500">*</span>
              <input
                required
                autoFocus
                value={draft.title}
                onChange={(e) => setValue("title", e.target.value)}
                className="mt-2 w-full rounded-lg border border-slate-300 px-3.5 py-3 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100"
                placeholder="What needs to be done?"
              />
            </label>
            <label className="block text-sm font-semibold text-slate-700">
              Description
              <div className="mt-2 overflow-hidden rounded-lg border border-slate-300">
                <div className="flex items-center gap-1 border-b border-slate-200 bg-slate-50 px-3 py-2 text-slate-500">
                  <span className="mr-3 text-sm">Paragraph</span>
                  <button type="button" className="rounded p-2 hover:bg-slate-200">
                    <FaBold />
                  </button>
                  <button type="button" className="rounded p-2 hover:bg-slate-200">
                    <FaUnderline />
                  </button>
                  <button type="button" className="rounded p-2 hover:bg-slate-200">
                    <FaListUl />
                  </button>
                </div>
                <textarea
                  value={draft.description}
                  onChange={(e) => setValue("description", e.target.value)}
                  rows={8}
                  className="w-full resize-y px-4 py-3 text-sm leading-6 outline-none"
                  placeholder="Add context, acceptance criteria, or implementation notes..."
                />
              </div>
            </label>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              <label className="text-sm font-semibold text-slate-700">
                Module
                <input
                  value={draft.module}
                  onChange={(e) => setValue("module", e.target.value)}
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm"
                />
              </label>
              <label className="text-sm font-semibold text-slate-700">
                Area
                <select
                  value={draft.area}
                  onChange={(e) => setValue("area", e.target.value)}
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm"
                >
                  {AREAS.map((area) => (
                    <option key={area}>{area}</option>
                  ))}
                </select>
              </label>
              <label className="text-sm font-semibold text-slate-700">
                Priority
                <select
                  value={draft.priority || "medium"}
                  onChange={(e) => setValue("priority", e.target.value)}
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm font-medium"
                >
                  {PRIORITIES.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm font-semibold text-slate-700">
                Status
                <select
                  value={draft.status}
                  onChange={(e) => setValue("status", e.target.value)}
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm font-medium"
                >
                  {availableStatuses.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <label className="text-sm font-semibold text-slate-700">
                Planned start
                <input
                  type="date"
                  min={create ? today : undefined}
                  value={draft.plannedStart || ""}
                  onChange={(e) => setValue("plannedStart", e.target.value)}
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm"
                />
              </label>
              <label className="text-sm font-semibold text-slate-700">
                Due date
                <input
                  type="date"
                  min={create ? (draft.plannedStart || today) : undefined}
                  value={draft.plannedEnd || ""}
                  onChange={(e) => setValue("plannedEnd", e.target.value)}
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm"
                />
              </label>
              <label className="text-sm font-semibold text-slate-700">
                Actual start
                <input
                  type="date"
                  min={create ? today : undefined}
                  value={draft.actualStart || ""}
                  onChange={(e) => setValue("actualStart", e.target.value)}
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm"
                />
              </label>
              {!create && (
                <label className="text-sm font-semibold text-slate-700">
                  Completed date {draft.status === "completed" && <span className="text-rose-500">*</span>}
                  <input
                    type="date"
                    max={today}
                    value={draft.actualEnd || ""}
                    onChange={(e) => setValue("actualEnd", e.target.value)}
                    required={draft.status === "completed"}
                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm"
                  />
                </label>
              )}
            </div>
          </div>
          <footer className="flex justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4 sm:px-8">
            <button
              type="button"
              onClick={cancelEdit}
              className="rounded-lg px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-200"
            >
              Cancel
            </button>
            <button
              disabled={saving}
              className="rounded-lg bg-blue-700 px-5 py-2.5 text-sm font-bold text-white hover:bg-blue-800 disabled:opacity-60"
            >
              {saving ? "Saving..." : create ? "Create" : "Save changes"}
            </button>
          </footer>
        </form>
      </main>
    );
  }

  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
      <Link
        to="/super-admin?panel=devActivityTracker"
        className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-blue-700"
      >
        <FaArrowLeft /> Back to activities
      </Link>
      <article className="mt-5 rounded-2xl border border-slate-200 bg-white shadow-sm">
        <header className="border-b border-slate-200 px-6 py-5 sm:px-8">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-blue-700">
              Development activities / {issueKey(activityId)}
            </p>
            <span
              className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-bold uppercase tracking-wider ${priorityBadgeStyle(
                activity.priority
              )}`}
            >
              {activity.priority || "medium"}
            </span>
          </div>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">{activity.title}</h1>
          <div className="mt-5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={beginEdit}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200"
            >
              <FaEdit /> Edit
            </button>
            <select
              value={activity.status}
              onChange={(e) => changeStatus(e.target.value)}
              className="rounded-lg border-0 bg-blue-50 px-3 py-2 text-sm font-bold text-blue-800"
            >
              {STATUSES.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-50"
            >
              <FaTrash /> Delete
            </button>
          </div>
        </header>
        <div className="grid gap-8 p-6 sm:p-8 lg:grid-cols-[1fr_20rem]">
          <div>
            <section>
              <h2 className="text-sm font-bold text-slate-800">Description</h2>
              <p className="mt-3 min-h-28 whitespace-pre-wrap rounded-xl bg-slate-50 p-4 text-sm leading-7 text-slate-600">
                {activity.description || "No description provided."}
              </p>
            </section>

            {/* Comment Thread Section */}
            <section className="mt-8 border-t border-slate-200 pt-6">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 text-sm font-bold text-slate-800">
                  <FaComments className="text-blue-600" /> Comments & Activity Thread
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                    {comments.length}
                  </span>
                </h2>
              </div>

              {/* Main New Comment Box */}
              <form onSubmit={handleAddComment} className="mt-4">
                <div className="rounded-xl border border-slate-300 bg-white p-3 shadow-sm focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100">
                  <textarea
                    rows={3}
                    value={newCommentBody}
                    onChange={(e) => setNewCommentBody(e.target.value)}
                    placeholder="Add a comment or implementation note..."
                    className="w-full resize-y text-sm outline-none placeholder:text-slate-400"
                  />
                  <div className="mt-2 flex justify-end">
                    <button
                      type="submit"
                      disabled={!newCommentBody.trim() || submittingComment}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-xs font-bold text-white transition hover:bg-blue-700 disabled:opacity-50"
                    >
                      <FaPaperPlane /> {submittingComment ? "Posting..." : "Comment"}
                    </button>
                  </div>
                </div>
              </form>

              {/* Threaded comments list */}
              <div className="mt-6 space-y-4">
                {loadingComments && (
                  <p className="py-4 text-center text-xs text-slate-500">Loading comments...</p>
                )}

                {!loadingComments && threadedComments.length === 0 && (
                  <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
                    No comments yet. Start the conversation or log updates above.
                  </div>
                )}

                {!loadingComments &&
                  threadedComments.map((comment) => {
                    const author =
                      comment.authorName ||
                      comment.userName ||
                      comment.author ||
                      comment.username ||
                      comment.createdBy ||
                      "Super Admin";
                    const isReplying = replyingToId === comment.id;

                    return (
                      <div
                        key={comment.id}
                        className="rounded-xl border border-slate-200 bg-slate-50/50 p-4 transition"
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-bold text-blue-700">
                            {author.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-bold text-slate-900">{author}</span>
                              <span className="text-[11px] text-slate-400">
                                {formatTimestamp(comment.createdAt || comment.timestamp)}
                              </span>
                            </div>
                            <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
                              {comment.body}
                            </p>
                            <button
                              type="button"
                              onClick={() => {
                                setReplyingToId(isReplying ? null : comment.id);
                                setReplyBody("");
                              }}
                              className="mt-2.5 inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800"
                            >
                              <FaReply className="text-[10px]" /> {isReplying ? "Cancel reply" : "Reply"}
                            </button>

                            {/* Inline reply composer */}
                            {isReplying && (
                              <div className="mt-3 rounded-lg border border-slate-300 bg-white p-3 shadow-sm">
                                <textarea
                                  autoFocus
                                  rows={2}
                                  value={replyBody}
                                  onChange={(e) => setReplyBody(e.target.value)}
                                  placeholder={`Reply to ${author}...`}
                                  className="w-full resize-none text-xs outline-none placeholder:text-slate-400"
                                />
                                <div className="mt-2 flex justify-end gap-2">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setReplyingToId(null);
                                      setReplyBody("");
                                    }}
                                    className="rounded px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    disabled={!replyBody.trim() || submittingReply}
                                    onClick={() => handlePostReply(comment.id)}
                                    className="rounded bg-blue-600 px-3 py-1 text-xs font-bold text-white hover:bg-blue-700 disabled:opacity-50"
                                  >
                                    {submittingReply ? "Posting..." : "Post reply"}
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* Render Nested Replies */}
                            {Array.isArray(comment.replies) && comment.replies.length > 0 && (
                              <div className="mt-3 space-y-3 border-l-2 border-slate-200 pl-4">
                                {comment.replies.map((reply) => {
                                  const replyAuthor =
                                    reply.authorName ||
                                    reply.userName ||
                                    reply.author ||
                                    reply.username ||
                                    reply.createdBy ||
                                    "Super Admin";
                                  return (
                                    <div key={reply.id} className="rounded-lg bg-white p-3 shadow-sm border border-slate-100">
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs font-bold text-slate-900">{replyAuthor}</span>
                                        <span className="text-[10px] text-slate-400">
                                          {formatTimestamp(reply.createdAt || reply.timestamp)}
                                        </span>
                                      </div>
                                      <p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-slate-700">
                                        {reply.body}
                                      </p>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </section>
          </div>
          <aside>
            <h2 className="text-sm font-bold text-slate-800">Details</h2>
            <dl className="mt-3 divide-y divide-slate-100 rounded-xl border border-slate-200">
              {[
                ["Type", "Task"],
                ["Priority", activity.priority ? activity.priority.toUpperCase() : "MEDIUM"],
                ["Status", STATUSES.find((x) => x.value === activity.status)?.label || activity.status],
                ["Module", activity.module],
                ["Area", activity.area],
                ["Planned start", activity.plannedStart],
                ["Due date", activity.plannedEnd],
                ["Actual start", activity.actualStart],
                ["Completed", activity.actualEnd],
              ].map(([label, value]) => (
                <div key={label} className="grid grid-cols-[7rem_1fr] gap-2 px-4 py-3 text-sm">
                  <dt className="text-slate-500">{label}</dt>
                  <dd className="font-semibold text-slate-800">{value || "Not set"}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-4 flex items-center gap-2 text-xs text-slate-400">
              <FaCalendarAlt /> Development delivery record
            </div>
          </aside>
        </div>
      </article>
      {confirmDelete && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/45 px-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-bold text-slate-900">Delete activity?</h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              This permanently removes “{activity.title}”.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setConfirmDelete(false)}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={remove}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-bold text-white hover:bg-rose-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
